import { useEffect, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  Divider,
  Grid,
  Group,
  Radio,
  Stack,
  Switch,
  Text,
} from "@mantine/core";

import { SupportedMediaType } from "apollo/graphql.generated";

import MintPreviewMediaUploader, {
  MediaFile,
} from "./MintPreviewMediaUploader";

/* eslint-disable  @typescript-eslint/no-explicit-any */
type SeriesMintStylePickerProps = {
  reveal: boolean;
  setReveal: (reveal: boolean) => void;
  previewImages: { url: string; type: string; name: string }[];
  setPreviewImages: (
    previewImages: { url: string; type: string; name: string }[]
  ) => void;
  showAllMetadata: boolean;
  setShowAllMetadata: (showAllMetadata: boolean) => void;
  mediaError: string;
};

const SeriesMintStylePicker = ({
  reveal,
  setReveal,
  previewImages,
  setPreviewImages,
  showAllMetadata,
  setShowAllMetadata,
  mediaError,
}: SeriesMintStylePickerProps) => {
  const [mediaFile, setMediaFile] = useState<MediaFile | null>(null);

  useEffect(() => {
    if (mediaFile) {
      setPreviewImages(previewImages.concat(mediaFile));
    }
  }, [mediaFile]);

  return (
    <>
      <Stack spacing={0}>
        <Text size="sm" component="label">
          Mint style
        </Text>
        <Text size="sm" mb={8}>
          Choose whether collectors can pick what tokens they want to mint
        </Text>

        <BoxContainer>
          <Radio
            styles={{
              body: {
                width: "100%",
                justifyContent: "space-between",
              },
              inner: { alignSelf: "center" },
              labelWrapper: { alignSelf: "center" },
            }}
            labelPosition="left"
            label={
              <Stack spacing={0}>
                <Group position="left" spacing={4}>
                  <Text size={15}>Collector&apos;s choice</Text>
                </Group>
                <Text size={13} color={TEXT_COLOR.SECONDARY}>
                  Collectors can see all tokens and choose which token they want
                  to mint
                </Text>
              </Stack>
            }
            checked={!reveal}
            onChange={(_) => setReveal(false)}
          ></Radio>
          <Divider my="sm" />
          <Radio
            styles={{
              body: {
                width: "100%",
                justifyContent: "space-between",
              },
              inner: { alignSelf: "center" },
              labelWrapper: { alignSelf: "center" },
            }}
            labelPosition="left"
            label={
              <Stack spacing={0}>
                <Group position="left" spacing={4}>
                  <Text size={15}>Random mint</Text>
                </Group>
                <Text size={13} color={TEXT_COLOR.SECONDARY}>
                  Collectors receive a random token from the collection when
                  they mint. We use an independently verifiable, deterministic
                  algorithm to ensure that the random selection is fair.
                </Text>
              </Stack>
            }
            checked={reveal}
            onChange={(_) => setReveal(true)}
          ></Radio>
          {reveal && (
            <>
              <Divider my="sm" />
              <Grid>
                <Group position="apart">
                  <Grid.Col span={10}>
                    <Stack spacing={0}>
                      <Group position="left" spacing={4}>
                        <Text size={15}>Hide series metadata</Text>
                      </Group>
                      <Text size={13} color={TEXT_COLOR.SECONDARY}>
                        Collectors will only be able to see the preview images
                        you choose before minting, not the entire series
                      </Text>
                    </Stack>
                  </Grid.Col>
                  <Switch
                    sx={{
                      input: {
                        height: "100%",
                      },
                    }}
                    color="dark"
                    checked={!showAllMetadata}
                    onChange={(event) => {
                      setShowAllMetadata(!event.currentTarget.checked);
                    }}
                    mr={8}
                  />
                </Group>
              </Grid>
            </>
          )}
        </BoxContainer>
      </Stack>
      {reveal && (
        <Stack spacing={0}>
          <Text weight={500} size="sm">
            Mint page preview images
          </Text>
          <Text size="sm" mb={8}>
            Optionally choose up to 7 images to showcase on your mint page. If
            you don&apos;t choose any, the first 7 token images will be used. If
            you choose an even number, the last one will not be shown in order
            to center the images.
          </Text>

          <MintPreviewMediaUploader
            mediaFiles={previewImages}
            onMediaAdded={(newMediaFile: MediaFile | null) => {
              if (newMediaFile) {
                setMediaFile(newMediaFile);
              }
            }}
            onMediaRemoved={(index: number) => {
              previewImages.splice(index, 1);
              setPreviewImages(previewImages);
            }}
            supportedTypes={[
              SupportedMediaType.IMAGE,
              SupportedMediaType.VIDEO,
            ]}
            mediaError={mediaError}
          />
        </Stack>
      )}
    </>
  );
};

export default SeriesMintStylePicker;
